/**
 * Zoom-video export: the signature feature of infinite-zoom art apps.
 * Animates the camera along a path (start view -> target view), capturing
 * the canvas with MediaRecorder into a WebM video.
 *
 * The flight is a log-space zoom + linear pan in the target cell's frame,
 * which produces the classic continuous "dive" of infinite zoom videos.
 */

import { Camera } from "./camera";
import { FrondEngine } from "./engine";

export interface ZoomVideoOptions {
  /** Target view. */
  target: { cellId: string; x: number; y: number };
  durationMs?: number;
  fps?: number;
  /** Extra zoom-in beyond physical-scale preservation (default 1). */
  zoomInFactor?: number;
  width?: number;
  height?: number;
  onProgress?: (t: number) => void;
}

export async function exportZoomVideo(
  engine: FrondEngine,
  opts: ZoomVideoOptions,
): Promise<Blob> {
  const duration = opts.durationMs ?? 5000;
  const fps = opts.fps ?? 30;
  const zoomInFactor = opts.zoomInFactor ?? 1;
  const width = opts.width ?? Math.round(engine.renderer.size.w);
  const height = opts.height ?? Math.round(engine.renderer.size.h);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D unavailable");

  const stream = canvas.captureStream(fps);
  const mime = pickMime();
  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  const camera = engine.camera;
  const snapshot = camera.state;

  // Express the source view in the target cell's frame.
  const tgtDepth = opts.target.cellId.length;
  const toTarget = engine.world.tree.transform(snapshot.focusCellId, opts.target.cellId);
  const srcPt = applyTo(snapshot, toTarget);
  const depthDelta = tgtDepth - snapshot.focusCellId.length;
  const srcScale = snapshot.scale * Math.pow(2, depthDelta);
  const tgtScale = srcScale * Math.pow(2, Math.max(0, -depthDelta)) * zoomInFactor;

  const stopped = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mime });
      resolve(blob);
    };
    recorder.onerror = () => reject(new Error("Recorder failed"));
  });

  const offscreen = new OffscreenRender(engine, canvas, ctx, width, height);
  recorder.start(200);

  const t0 = performance.now();
  await new Promise<void>((resolve) => {
    const step = () => {
      const t = Math.min(1, (performance.now() - t0) / duration);
      const eased = easeInOutCubic(t);
      camera.focusCellId = opts.target.cellId;
      camera.x = srcPt.x + (opts.target.x - srcPt.x) * eased;
      camera.y = srcPt.y + (opts.target.y - srcPt.y) * eased;
      camera.scale = srcScale * Math.pow(tgtScale / srcScale, eased);
      camera.rotation = snapshot.rotation * (1 - eased);
      offscreen.frame(camera);
      opts.onProgress?.(t);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        offscreen.frame(camera);
        // Restore the original camera.
        camera.focusCellId = snapshot.focusCellId;
        camera.x = snapshot.x;
        camera.y = snapshot.y;
        camera.scale = snapshot.scale;
        camera.rotation = snapshot.rotation;
        engine.requestRender();
        resolve();
      }
    };
    requestAnimationFrame(step);
  });

  recorder.stop();
  return stopped;
}

/** Renders the engine scene into a standalone canvas (not the app canvas). */
class OffscreenRender {
  constructor(
    private engine: FrondEngine,
    private canvas: HTMLCanvasElement,
    private ctx: CanvasRenderingContext2D,
    private w: number,
    private h: number,
  ) {}

  frame(camera: Camera): void {
    // Temporarily render via the engine's renderer into our context.
    this.engine.renderer.render(camera, {
      ctx: this.ctx,
      width: this.w,
      height: this.h,
      dpr: 1,
    });
  }
}

function applyTo(
  snap: { focusCellId: string; x: number; y: number; scale: number; rotation: number },
  m: { a: number; b: number; c: number; d: number; e: number; f: number },
): { x: number; y: number } {
  return {
    x: m.a * snap.x + m.c * snap.y + m.e,
    y: m.b * snap.x + m.d * snap.y + m.f,
  };
}

function pickMime(): string {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(c)) return c;
  }
  return "";
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
